import { useCallback, useEffect, useState } from "react";
import { dashFetch } from "@/api/client";
import { Card } from "@/components/Card";
import type { SettingsSkeleton } from "@/types/account";

const EMPTY: SettingsSkeleton = {
  id: "",
  name: "",
  description: "",
  generators: [],
  settingsPatch: {},
  firewallPatch: { waf: { enabled: true } }
};

export default function SettingsSkeletonsPanel() {
  const [skeletons, setSkeletons] = useState<SettingsSkeleton[]>([]);
  const [draft, setDraft] = useState<SettingsSkeleton>(EMPTY);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await dashFetch<{ ok?: boolean; skeletons?: SettingsSkeleton[] }>(
      "/dashboard/api/account/settings-skeletons",
      { cache: "no-store" }
    );
    setSkeletons(Array.isArray(r?.skeletons) ? r.skeletons : []);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const select = (sk: SettingsSkeleton) => {
    setDraft({
      ...sk,
      generators: [...(sk.generators || [])],
      settingsPatch: sk.settingsPatch || {},
      firewallPatch: sk.firewallPatch || {}
    });
    setMsg("");
  };

  const save = async () => {
    setBusy(true);
    setMsg("");
    try {
      let settingsPatch = draft.settingsPatch;
      let firewallPatch = draft.firewallPatch;
      if (typeof settingsPatch === "string") settingsPatch = JSON.parse(settingsPatch);
      if (typeof firewallPatch === "string") firewallPatch = JSON.parse(firewallPatch);
      const r = await dashFetch<{ ok?: boolean; skeleton?: SettingsSkeleton; error?: string }>(
        "/dashboard/api/account/settings-skeletons",
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            ...draft,
            generators: String(draft.generators || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean),
            settingsPatch,
            firewallPatch
          })
        }
      );
      if (!r?.ok) throw new Error(String(r?.error || "save failed"));
      setMsg("Saved.");
      await load();
      if (r.skeleton) select(r.skeleton);
    } catch (e) {
      setMsg(String(e instanceof Error ? e.message : e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm(`Delete skeleton "${id}"?`)) return;
    setBusy(true);
    try {
      await dashFetch(`/dashboard/api/account/settings-skeletons?id=${encodeURIComponent(id)}`, {
        method: "DELETE"
      });
      await load();
      if (draft.id === id) setDraft(EMPTY);
      setMsg("Deleted.");
    } catch (e) {
      setMsg(String(e instanceof Error ? e.message : e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Settings presets">
      <p className="muted" style={{ marginTop: 0 }}>
        Reusable setting bundles applied during the add-site wizard (e.g. Default WordPress). Sites match by detected generator.
      </p>
      <div className="skeletonAdminLayout">
        <div className="skeletonAdminList">
          {skeletons.map((sk) => (
            <button
              key={sk.id}
              type="button"
              className={"skeletonAdminItem" + (draft.id === sk.id ? " is-active" : "")}
              onClick={() => select(sk)}
            >
              <strong>{sk.name}</strong>
              <span className="muted mono">{sk.id}</span>
              <span className="muted">{sk.generators?.join(", ") || "any"}</span>
            </button>
          ))}
          <button type="button" className="btn" onClick={() => select(EMPTY)}>Add preset</button>
        </div>
        <div className="skeletonAdminEditor">
          <div className="field">
            <label>ID</label>
            <input
              className="settingsInput settingsMono"
              value={draft.id}
              onChange={(e) => setDraft((d) => ({ ...d, id: e.target.value }))}
              placeholder="default-wordpress"
            />
          </div>
          <div className="field">
            <label>Name</label>
            <input
              className="settingsInput"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
          </div>
          <div className="field">
            <label>Description</label>
            <textarea
              className="settingsInput"
              rows={2}
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            />
          </div>
          <div className="field">
            <label>Generators (comma-separated)</label>
            <input
              className="settingsInput settingsMono"
              value={Array.isArray(draft.generators) ? draft.generators.join(", ") : ""}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  generators: e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
                }))
              }
              placeholder="wordpress"
            />
          </div>
          <div className="field">
            <label>Settings patch (JSON)</label>
            <textarea
              className="settingsInput settingsMono"
              rows={10}
              value={JSON.stringify(draft.settingsPatch || {}, null, 2)}
              onChange={(e) => {
                try {
                  setDraft((d) => ({ ...d, settingsPatch: JSON.parse(e.target.value) }));
                } catch {
                  /* allow invalid while typing via string hack - skip */
                }
              }}
            />
          </div>
          <div className="field">
            <label>Firewall patch (JSON)</label>
            <textarea
              className="settingsInput settingsMono"
              rows={6}
              value={JSON.stringify(draft.firewallPatch || {}, null, 2)}
              onChange={(e) => {
                try {
                  setDraft((d) => ({ ...d, firewallPatch: JSON.parse(e.target.value) }));
                } catch {
                  /* ignore */
                }
              }}
            />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="btn primary" disabled={busy || !draft.id.trim()} onClick={() => void save()}>
              Save skeleton
            </button>
            {draft.id ? (
              <button type="button" className="btn danger" disabled={busy} onClick={() => void remove(draft.id)}>
                Delete
              </button>
            ) : null}
          </div>
          {msg ? <p className="muted" style={{ marginTop: 10 }}>{msg}</p> : null}
        </div>
      </div>
    </Card>
  );
}
